import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import AuthUserContainer from '../../containers/AuthUser';
import { search, runPending, rerun, setAutoRun } from '../../modules/system/migrations';

const styles = (theme) => ({
	innerPaper: {
		padding: 10,
		textAlign: 'left',
		fontSize: 14,
		[theme.breakpoints.up('md')]: {
			padding: 40,
			paddingTop: 30,
		},
	},
	description: {
		color: '#666',
		marginBottom: 20,
	},
	pendingBanner: {
		background: '#FFF8E1',
		border: '1px solid #FFE082',
		borderRadius: 4,
		padding: '8px 12px',
		margin: '16px 0px',
		fontSize: 14,
	},
	errorBanner: {
		background: '#FFEBEE',
		border: '1px solid #EF9A9A',
		borderRadius: 4,
		padding: '8px 12px',
		margin: '16px 0px',
		fontSize: 14,
	},
	successBanner: {
		background: '#E8F5E9',
		border: '1px solid #A5D6A7',
		borderRadius: 4,
		padding: '8px 12px',
		margin: '16px 0px',
		fontSize: 14,
	},
	errorMessage: {
		whiteSpace: 'pre-wrap',
		fontFamily: 'monospace',
		fontSize: 12,
		color: '#b71c1c',
	},
	chipApplied: {
		background: '#A5D6A7',
	},
	chipPending: {
		background: '#FFE082',
	},
	chipFailed: {
		background: '#EF9A9A',
	},
	chipBaseline: {
		background: '#B0BEC5',
	},
});

class Migrations extends Component {
	constructor(props) {
		super(props);
		this.state = {
			result: undefined,
			isWorking: false,
			lastApplied: undefined,
		};
	}

	componentDidMount() {
		this.loadStatus();
	}

	loadStatus = () => {
		search({
			callback: (err, r) => {
				if (err) {
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ result: r });
			},
		});
	};

	handleRunResult = (err, r) => {
		this.setState({ isWorking: false });
		if (err) {
			return this.props.showErrors(_.isArray(err) ? err : [err]);
		}
		this.setState({ result: r, lastApplied: _.get(r, 'applied', []) });
	};

	handleRunPending = () => {
		this.setState({ isWorking: true, lastApplied: undefined });
		runPending({ callback: this.handleRunResult });
	};

	handleRerun = (migrationName) => {
		this.setState({ isWorking: true, lastApplied: undefined });
		rerun({ migrationName, callback: this.handleRunResult });
	};

	handleAutoRunToggle = (event) => {
		const enabled = event.target.checked;
		this.setState({ isWorking: true });
		setAutoRun({
			enabled,
			callback: (err, r) => {
				this.setState({ isWorking: false });
				if (err) {
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ result: r });
			},
		});
	};

	statusChip = (migration) => {
		const { classes } = this.props;
		const classByStatus = {
			applied: classes.chipApplied,
			pending: classes.chipPending,
			failed: classes.chipFailed,
			baseline: classes.chipBaseline,
		};
		return (
			<Chip
				size="small"
				label={migration.status}
				className={classByStatus[migration.status] || undefined}
			/>
		);
	};

	renderBanners = () => {
		const { classes } = this.props;
		const { result, lastApplied } = this.state;
		const pendingCount = _.get(result, 'pendingCount', 0);
		const failedCount = _.get(result, 'failedCount', 0);
		const runError = _.get(result, 'runError');

		return (
			<div>
				{!_.isEmpty(lastApplied) && (
					<div className={classes.successBanner}>
						Applied {lastApplied.length}: {lastApplied.join(', ')}
					</div>
				)}
				{runError && (
					<div className={classes.errorBanner}>
						Run stopped: <span className={classes.errorMessage}>{runError}</span>
					</div>
				)}
				{failedCount > 0 && (
					<div className={classes.errorBanner}>
						{failedCount} failed migration{failedCount > 1 ? 's' : ''} blocking the chain — fix the
						file, then Re-run.
					</div>
				)}
				{pendingCount > 0 && failedCount === 0 && (
					<div className={classes.pendingBanner}>
						{pendingCount} migration{pendingCount > 1 ? 's' : ''} pending.
					</div>
				)}
			</div>
		);
	};

	render() {
		const { classes } = this.props;
		const { result, isWorking } = this.state;

		if (!result) {
			return (
				<Paper>
					<div className={classes.innerPaper}>Loading migrations...</div>
				</Paper>
			);
		}

		//- Latest first, so a glance after a deploy confirms the recent ones ran.
		//- Display only -- the runner always applies in ascending (chronological) order.
		const migrations = _.orderBy(
			_.get(result, ['collections', 'schemaMigrationCollection'], []),
			['migrationName'],
			['desc']
		);
		const pendingCount = _.get(result, 'pendingCount', 0);

		return (
			<Paper>
				<div className={classes.innerPaper}>
					<Typography variant="h5" gutterBottom>
						Schema Migrations
					</Typography>
					<div className={classes.description}>
						Every file in sql/migrations/ with its applied status from the schemaMigrations table.
						Run Pending applies them in order under a database lock; a failure stops the chain and
						emails dwp. Auto Run makes every server apply pending migrations at boot (i.e. on
						deploy).
					</div>

					<FormControlLabel
						control={
							<Switch
								checked={Boolean(result.autoRun)}
								onChange={this.handleAutoRunToggle}
								disabled={isWorking}
								color="primary"
							/>
						}
						label={`Auto Run at boot: ${result.autoRun ? 'ON' : 'OFF'}`}
					/>

					{this.renderBanners()}

					<Button
						color="primary"
						variant="contained"
						disabled={isWorking || pendingCount === 0}
						onClick={this.handleRunPending}
					>
						{isWorking ? 'Working...' : `Run Pending (${pendingCount})`}
					</Button>

					<Table size="small" style={{ marginTop: 24 }}>
						<TableHead>
							<TableRow>
								<TableCell>Migration</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Applied</TableCell>
								<TableCell>Duration</TableCell>
								<TableCell>Host</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{migrations.map((migration) => (
								<TableRow key={migration.migrationName}>
									<TableCell>
										{migration.migrationName}
										{migration.checksumChanged && (
											<div className={classes.errorMessage}>
												<span role="img" aria-label="warning">
													⚠️
												</span>{' '}
												file edited after it was applied
											</div>
										)}
										{migration.fileMissing && (
											<div className={classes.errorMessage}>
												<span role="img" aria-label="warning">
													⚠️
												</span>{' '}
												file missing from deploy
											</div>
										)}
									</TableCell>
									<TableCell>{this.statusChip(migration)}</TableCell>
									<TableCell>
										{migration.description}
										{migration.errorMessage && (
											<div className={classes.errorMessage}>{migration.errorMessage}</div>
										)}
									</TableCell>
									<TableCell>{migration.dateApplied || '-'}</TableCell>
									<TableCell>
										{_.isNumber(migration.durationMs) ? `${migration.durationMs}ms` : '-'}
									</TableCell>
									<TableCell>{migration.appliedByHost || '-'}</TableCell>
									<TableCell>
										{migration.status === 'failed' && (
											<Button
												size="small"
												variant="outlined"
												disabled={isWorking}
												onClick={() => this.handleRerun(migration.migrationName)}
											>
												Re-run
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</Paper>
		);
	}
}

Migrations.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), AuthUserContainer])(Migrations);
